/**
 * Cart A/B Testing Manager
 * Dynamically switches between cart drawer and popup notification
 * Supports URL parameters and cookie persistence
 */

class CartABTesting {
  constructor() {
    this.cookieName = 'cart_type_test';
    this.defaultCartType = 'notification'; // fallback to theme default
    this.validCartTypes = ['drawer', 'notification'];
    this.currentCartType = null;
    
    // Initialize on DOM ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  /**
   * Initialize the A/B testing system
   */
  init() {
    // Wait for elements to be ready
    this.waitForElements().then(() => {
      // Determine which cart type to use
      this.currentCartType = this.determineCartType();
      
      // Apply the cart type
      this.applyCartType(this.currentCartType);
      
      // Set up event listeners
      this.setupEventListeners();
    });
  }

  /**
   * Wait for cart elements to be available in DOM
   */
  async waitForElements() {
    const maxAttempts = 50;
    let attempts = 0;
    
    while (attempts < maxAttempts) {
      const cartDrawer = document.querySelector('cart-drawer');
      const cartNotification = document.querySelector('cart-notification');
      const productForms = document.querySelectorAll('product-form');
      
      if (cartDrawer && cartNotification && productForms.length > 0) {
        return;
      }
      
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }
  }

  /**
   * Determine which cart type to use based on URL params, cookies, or default
   * Priority: URL parameter > VWO/Optimizely > Cookie > Theme Default
   */
  determineCartType() {
    // Check URL parameters first
    const urlParams = new URLSearchParams(window.location.search);
    const urlCartType = urlParams.get('cart_type');
    
    if (urlCartType && this.validCartTypes.includes(urlCartType)) {
      this.setCookie(urlCartType);
      return urlCartType;
    }

    // Check for VWO/Optimizely integration
    const vwoCartType = this.getVWOCartType();
    if (vwoCartType) {
      this.setCookie(vwoCartType);
      return vwoCartType;
    }

    // Check cookie
    const cookieCartType = this.getCookie();
    if (cookieCartType && this.validCartTypes.includes(cookieCartType)) {
      return cookieCartType;
    }

    // Fall back to theme default
    const themeDefault = window.themeSettings?.cart_type || this.defaultCartType;
    return themeDefault;
  }

  /**
   * Check for VWO/Optimizely variables (customize based on your A/B testing tool)
   */
  getVWOCartType() {
    // VWO Integration
    if (typeof window.VWO !== 'undefined' && window.VWO.data) {
      // Replace 'cart_type_variation' with your actual VWO variable name
      const vwoCartType = window.VWO.data.vi?.cart_type_variation;
      if (vwoCartType && this.validCartTypes.includes(vwoCartType)) {
        return vwoCartType;
      }
    }

    // Optimizely Integration
    if (typeof window.optimizely !== 'undefined') {
      try {
        const optimizelyData = window.optimizely.get('data');
        // Replace with your actual Optimizely custom variable
        const optimizelyCartType = optimizelyData?.cart_type_test;
        if (optimizelyCartType && this.validCartTypes.includes(optimizelyCartType)) {
          return optimizelyCartType;
        }
      } catch (e) {
      }
    }

    // Google Optimize Integration
    if (typeof window.gtag !== 'undefined') {
      // This would need to be set up with your Google Optimize experiment
      // Example: gtag('event', 'custom_variant', { cart_type: 'drawer' });
    }

    return null;
  }

  /**
   * Apply the specified cart type
   */
  applyCartType(cartType) {
    
    // Clear section cache to prevent wrong sections from being used
    if (window.cartSectionManager) {
      window.cartSectionManager.invalidateCache();
    }
    
    // Hide all cart types first
    this.hideAllCartTypes();
    
    // Show the selected cart type and set up behavior
    if (cartType === 'drawer') {
      this.enableCartDrawer();
    } else if (cartType === 'notification') {
      this.enableCartNotification();
    }

    // Update any settings references
    this.updateSettingsReferences(cartType);
    
    // Fix product forms to use the correct cart element
    this.updateProductFormCartReferences(cartType);
    
    // Emit event for other components to react to cart type change
    document.dispatchEvent(new CustomEvent('cart-type-changed', {
      detail: { 
        cartType: cartType,
        previousType: this.previousCartType || this.currentCartType
      }
    }));
    
    this.previousCartType = this.currentCartType;
  }

  /**
   * Hide all cart types
   */
  hideAllCartTypes() {
    // Mark cart drawer as inactive
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.style.display = 'none';
      cartDrawer.setAttribute('data-cart-ab-inactive', 'true');
    }

    // Mark cart notification as inactive
    const cartNotification = document.querySelector('cart-notification');
    if (cartNotification) {
      cartNotification.style.display = 'none';
      cartNotification.setAttribute('data-cart-ab-inactive', 'true');
    }
  }

  /**
   * Enable cart drawer
   */
  enableCartDrawer() {
    
    // Show cart drawer and mark as active
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.style.display = '';
      cartDrawer.removeAttribute('data-cart-ab-inactive');
    }

    // Update cart icon click behavior to open drawer
    this.updateCartIconBehavior('drawer');
  }

  /**
   * Enable cart notification
   */
  enableCartNotification() {
    
    // Show cart notification and mark as active
    const cartNotification = document.querySelector('cart-notification');
    if (cartNotification) {
      cartNotification.style.display = '';
      cartNotification.removeAttribute('data-cart-ab-inactive');
    }

    // Update cart icon click behavior for notification
    this.updateCartIconBehavior('notification');
  }

  /**
   * Update cart icon click behavior
   */
  updateCartIconBehavior(cartType) {
    const cartIcon = document.querySelector('#cart-icon-bubble');
    if (!cartIcon) {
      return;
    }

    // Remove existing event listeners by cloning the element
    const newCartIcon = cartIcon.cloneNode(true);
    cartIcon.parentNode.replaceChild(newCartIcon, cartIcon);

    if (cartType === 'drawer') {
      // Drawer behavior - open drawer on click
      newCartIcon.addEventListener('click', (event) => {
        event.preventDefault();
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open(newCartIcon);
        } else {
        }
      });

      // Also update keyboard accessibility
      newCartIcon.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE' || event.code.toUpperCase() === 'ENTER') {
          event.preventDefault();
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open(newCartIcon);
          }
        }
      });
    } else {
      // Notification behavior - go to cart page (default behavior)
      // No need to add click handler, let the default href work
    }
  }

  /**
   * Update settings references for theme compatibility
   */
  updateSettingsReferences(cartType) {
    // Update window.themeSettings if it exists
    if (window.themeSettings) {
      window.themeSettings.cart_type = cartType;
    }

    // Dispatch custom event for other scripts to listen to
    document.dispatchEvent(new CustomEvent('cart-type-changed', {
      detail: { 
        cartType: cartType,
        previousType: this.currentCartType
      }
    }));
  }

  /**
   * Update product forms to use the correct cart element
   */
  updateProductFormCartReferences(cartType) {
    
    // Get the active cart element
    const correctCart = this.getActiveCart();
    
    if (!correctCart) {

      return;
    }
    
    
    // Find all product forms and update their cart references
    const productForms = document.querySelectorAll('product-form');
    
    productForms.forEach((productForm, index) => {
      const oldCart = productForm.cart;
      const oldCartType = oldCart ? oldCart.tagName.toLowerCase() : 'none';
      
      // Update the product form's cart reference
      productForm.cart = correctCart;
      
      
      // Also verify the cart's getSectionsToRender method
      if (correctCart.getSectionsToRender) {
        const sections = correctCart.getSectionsToRender();
      }
    });

    // Handle any existing add-to-cart functionality that might be using the wrong cart
    this.overrideStickyAddToCartBehavior(cartType);
    
    // Force refresh cart contents with correct sections
    this.refreshCartContents(correctCart);
  }
  
  /**
   * Force refresh cart contents with the correct sections for the active cart type
   */
  async refreshCartContents(cart) {
    if (!cart || !cart.getSectionsToRender) return;
    
    try {
      const refreshStartTime = Date.now();
      
      const sections = cart.getSectionsToRender();
      const sectionIds = sections.map(section => section.id).join(',');
      
      // Fetch cart with the correct sections - add cache busting to force fresh data
      const cacheBuster = Date.now();
      const response = await fetch(`${window.Shopify.routes.root}cart?sections=${sectionIds}&_t=${cacheBuster}`);
      
      if (!response.ok) throw new Error('Failed to fetch cart');
      
      const data = await response.json();
      
      // Trigger cart rendering with the new data
      if (cart.renderContents && data.sections) {
        
        // CRITICAL: Check if A/B testing refresh is returning wrong content
        if (data.sections['cart-drawer']) {
          const content = data.sections['cart-drawer'];
          const hasContinueShopping = content.includes('Continue shopping');
          const hasTitleWrapper = content.includes('title-wrapper-with-link');
          

          
          if (hasContinueShopping || hasTitleWrapper) {

          } else {
          }
        }
        
        cart.renderContents(data);
        
        const refreshEndTime = Date.now();

      }
      
    } catch (error) {
      console.error('Cart A/B Testing: Failed to refresh cart contents:', error);
    }
  }

  /**
   * Get the currently active cart element (for use by other scripts)
   */
  getActiveCart() {
    if (this.currentCartType === 'drawer') {
      const cartDrawer = document.querySelector('cart-drawer:not([data-cart-ab-inactive])');
      return cartDrawer;
    } else {
      const cartNotification = document.querySelector('cart-notification:not([data-cart-ab-inactive])');
      return cartNotification;
    }
  }

  /**
   * Override sticky add-to-cart behavior to use the correct cart
   */
  overrideStickyAddToCartBehavior(cartType) {
    
    // Create a global function that other scripts can use to get the correct cart
    window.getCartElement = () => {
      return this.getActiveCart() || document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    };
  }

  /**
   * Set up event listeners
   */
  setupEventListeners() {
    // Listen for URL changes (for SPA-like behavior)
    window.addEventListener('popstate', () => {
      const newCartType = this.determineCartType();
      if (newCartType !== this.currentCartType) {
        this.currentCartType = newCartType;
        this.applyCartType(newCartType);
      }
    });

    // Listen for custom events from external A/B testing tools
    document.addEventListener('vwo-variation-applied', (event) => {
      if (event.detail && event.detail.cart_type) {
        this.currentCartType = event.detail.cart_type;
        this.applyCartType(this.currentCartType);
      }
    });

    // Listen for cart updates to ensure proper display
    document.addEventListener('cart:updated', () => {
      // Refresh the current cart type to ensure proper display
      setTimeout(() => {
        this.applyCartType(this.currentCartType);
      }, 100);
    });

    // Listen for Shopify's cart update events
    document.addEventListener('shopify:section:load', () => { 
      // Re-apply cart type after section reloads
      setTimeout(() => {
        this.applyCartType(this.currentCartType);
      }, 100);
    });
  }

  /**
   * Cookie management
   */
  setCookie(cartType) {
    const expires = new Date();
    expires.setTime(expires.getTime() + (30 * 24 * 60 * 60 * 1000)); // 30 days
    document.cookie = `${this.cookieName}=${cartType}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
  }

  getCookie() {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === this.cookieName) {
        return value;
      }
    }
    return null;
  }

  /**
   * Public API methods
   */
  switchToDrawer() {
    this.setCookie('drawer');
    this.currentCartType = 'drawer';
    this.applyCartType('drawer');
  }

  switchToNotification() {
    this.setCookie('notification');
    this.currentCartType = 'notification';
    this.applyCartType('notification');
  }

  getCurrentCartType() {
    return this.currentCartType;
  }

  /**
   * Force update all product forms to use the correct cart (for debugging)
   */
  forceUpdateProductForms() {
    this.updateProductFormCartReferences(this.currentCartType);
  }

  /**
   * Debug method to check system status
   */
  getDebugInfo() {
    const productForms = document.querySelectorAll('product-form');
    const productFormCartTypes = Array.from(productForms).map(form => {
      if (!form.cart) return 'none';
      if (form.cart.tagName === 'CART-DRAWER') return 'drawer';
      if (form.cart.tagName === 'CART-NOTIFICATION') return 'notification';
      return 'unknown';
    });

    return {
      currentCartType: this.currentCartType,
      cookie: this.getCookie(),
      themeDefault: window.themeSettings?.cart_type,
      cartDrawerExists: !!document.querySelector('cart-drawer'),
      cartNotificationExists: !!document.querySelector('cart-notification'),
      cartIconExists: !!document.querySelector('#cart-icon-bubble'),
      cartDrawerVisible: document.querySelector('cart-drawer')?.style.display !== 'none',
      cartNotificationVisible: document.querySelector('cart-notification')?.style.display !== 'none',
      productFormsCount: productForms.length,
      productFormCartTypes: productFormCartTypes
    };
  }
}

// Initialize the cart A/B testing system
window.cartABTesting = new CartABTesting();

// Expose public API for external tools
window.CartABTestingAPI = {
  switchToDrawer: () => window.cartABTesting.switchToDrawer(),
  switchToNotification: () => window.cartABTesting.switchToNotification(),
  getCurrentType: () => window.cartABTesting.getCurrentCartType(),
  getActiveCart: () => window.cartABTesting.getActiveCart(),
  forceUpdateProductForms: () => window.cartABTesting.forceUpdateProductForms(),
  getDebugInfo: () => window.cartABTesting.getDebugInfo()
};

// For debugging in console
